import { Campaign, getSiteConfig, Locale, sites } from "../config/site";
import { inProduction } from "../../config";
import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";

export type Domain = "lassie.de" | "lassie.co" | "lassie.fr" | "lassie.co.uk";
export type Org = "ger" | "swe" | "fra" | "gbr";
export enum Domains {
  de = "lassie.de",
  co = "lassie.co",
  fr = "lassie.fr",
  uk = "lassie.co.uk",
}

/*
Dont try to rewrite this into something more fancy,
the env vars needs to be written like this to be understood by the interpreter
 */
export const getDomain = (): Domain => {
  const frontend = process.env.NEXT_PUBLIC_DOMAIN;
  const backend = process.env.DOMAIN;

  const resolvedDomain = backend || frontend;

  if (resolvedDomain === Domains.de) {
    return Domains.de;
  }
  if (resolvedDomain === Domains.co) {
    return Domains.co;
  }
  if (resolvedDomain === Domains.fr) {
    return Domains.fr;
  }
  if (resolvedDomain === Domains.uk) {
    return Domains.uk;
  }

  throw new Error("Valid domain env var not found");
};

export const getSiteUrl = (path?: string | null): string => {
  const trimmedPath = (path ?? "").trim();
  const cleanedPath = trimmedPath.startsWith("/")
    ? trimmedPath.substring(1)
    : trimmedPath;

  const base = process.env.NEXT_PUBLIC_SITE_URL || `https://www.${getDomain()}`;
  return cleanedPath ? `${base}/${cleanedPath}` : base;
};

// SEO-facing urls (sitemap, robots, canonical, og:url) must always be built on
// the host the market is actually served on. getSiteUrl's env var (and its
// `www.<domain>` fallback, a redirect host for every market but SWE) must never
// leak into them — a redirect-only sitemap is what broke UK indexing.
export const getResolvedSiteUrl = (path?: string | null): string => {
  const trimmedPath = (path ?? "").trim();
  const cleanedPath = trimmedPath.startsWith("/")
    ? trimmedPath.substring(1)
    : trimmedPath;

  const base = `https://${getResolvedDomain()}`;
  return cleanedPath ? `${base}/${cleanedPath}` : base;
};

// Next.js serves each market's default locale unprefixed, so only the secondary
// locales carry a /<locale>/ segment — the same rule useCanonicalUrl follows.
// Prefixing regardless yields urls that only redirect to the canonical one, and
// for a market whose default locale *is* "en" it invents a whole /en/ tree.
export const getLocalizedSiteUrl = (path: string, locale: Locale): string => {
  const prefix = locale === getDefaultLocale() ? "" : `${locale}/`;

  // The secondary tree's own root is served at /<locale>, with no trailing
  // slash. A "/" path would otherwise compose to "<locale>/", listing a
  // redirect as the home page of the tree.
  const composed = `${prefix}${path.replace(/^\/+/, "")}`.replace(/\/$/, "");

  return new URL(composed, getResolvedSiteUrl()).toString();
};

export const getOrg = (): Org => {
  const frontend = process.env.NEXT_PUBLIC_DOMAIN;
  const backend = process.env.DOMAIN;

  const resolvedDomain = backend || frontend;

  if (resolvedDomain === Domains.de) {
    return "ger";
  }
  if (resolvedDomain === Domains.co) {
    return "swe";
  }
  if (resolvedDomain === Domains.fr) {
    return "fra";
  }
  if (resolvedDomain === Domains.uk) {
    return "gbr";
  }

  throw new Error("Valid domain env var not found");
};

export const isOrg = (org: Org): boolean => org === getOrg();

export const getLocales = (): Array<Locale> => getSiteConfig()["locales"];

export const getDefaultLocale = (): Locale => getSiteConfig()["defaultLocale"];

export const stripeKey = (): string => {
  return process.env.NEXT_PUBLIC_STRIPE_PUBLIC_KEY ?? "";
};

export const getAppStoreUrl = () => getSiteConfig()["appStoreUrl"];

export const getPlayMarketUrl = () =>
  "https://play.google.com/store/apps/details?id=com.teamlassie.lassieapp";

export const getTagManagerId = () => {
  return getSiteConfig()["tagManagerId"][
    inProduction ? "production" : "development"
  ];
};

export const getAuthenticationMethod = () => {
  return getSiteConfig()["authentication"];
};

export const getCurrencyCode = () => getSiteConfig()["currency"]["code"];
export const getCurrencySymbol = () => getSiteConfig()["currency"]["symbol"];
export const getUserCentricId = () => getSiteConfig()["userCentricId"];

export const getCountryCode = () => getSiteConfig()["countryCode"];

export const getStartOffsetInDays = () =>
  getSiteConfig()["insurance"]["startOffsetInDays"];

// Max days into the future a policy start date may be set — set per market in
// the site config (see getLastAllowedStartDate).
export const getMaxStartOffsetInDays = () =>
  getSiteConfig()["insurance"]["maxStartOffsetInDays"];

export const getMinimumAgeInDays = () =>
  getSiteConfig()["insurance"]["minimumAgeInDays"];

export const getSelectablePaymentMethods = () =>
  getSiteConfig()["payment"]["selectablePaymentMethods"];
export const getSelectablePaymentDays = () =>
  getSiteConfig()["payment"]["selectableBillingDays"];

export const getMeetingBookingUrl = () => {
  const meetingBookingUrl =
    getSiteConfig()["contactDetails"]["meetingBookingUrl"];

  return meetingBookingUrl !== "" ? meetingBookingUrl : null;
};
export const getContactEmail = () => getSiteConfig()["contactDetails"]["email"];

export const getDateFormat = () => getSiteConfig()["dateFormat"];

export const getCopays = () => getSiteConfig()["insurance"]["copays"];
export const getDeductibles = () => getSiteConfig()["insurance"]["deductibles"];
export const getPreventiveCareAddOnLimits = () =>
  getSiteConfig()["insurance"]["preventiveCareAddOnLimits"] || [];
export const getLimits = () => getSiteConfig()["insurance"]["limits"] || [];

export const getInsurancePackage = (product: SignUpProductEnum) =>
  getSiteConfig()["insurancePackages"][product];

export const getDefaultCopay = () =>
  getSiteConfig()["insurance"]["defaultCopay"];
export const getDefaultDeductible = () =>
  getSiteConfig()["insurance"]["defaultDeducible"];
export const getDefaultLimit = () =>
  getSiteConfig()["insurance"]["defaultLimit"];
export const getDefaultBillingDay = () =>
  getSiteConfig()["insurance"]["defaultBillingDay"];

export const getResolvedDomainByOrg = (org: Org) => sites[org].resolvedDomain;
export const getResolvedDomain = () => getSiteConfig().resolvedDomain;

/**
 * Campaign codes flattened to `{ puppy, kitten, multipet }`, for looking a
 * discount code up by campaign.
 */
export const getCampaigns = ():
  | Partial<Record<Campaign, string>>
  | undefined => {
  const campaigns = getSiteConfig().campaigns;

  if (!campaigns) {
    return;
  }

  const { multipet, youngAnimal } = campaigns;

  return {
    ...(multipet ? { multipet } : {}),
    ...(youngAnimal?.puppy ? { puppy: youngAnimal.puppy } : {}),
    ...(youngAnimal?.kitten ? { kitten: youngAnimal.kitten } : {}),
  };
};

/** Undefined in markets that don't run a puppy/kitten discount. */
export const getYoungAnimalCampaign = () =>
  getSiteConfig().campaigns?.youngAnimal;

export const getLeadAllianceProgramId = () =>
  getSiteConfig()["leadAllianceProgramId"];

export const getSocialLadderSettings = () => getSiteConfig().socialLadder;
